import { ReactNode, lazy, Suspense } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

import LoaderOverlay from 'src/components/Loader';
import HomeLayout from 'src/common/layout/HomeLayout';
import AppLogoLayout from 'src/common/layout/AppLogoLayout';

interface LayoutWrapperProps {
  children: ReactNode;
}

const detailRoutes = [
  '/address/new',
  '/addresslist',
  '/profile',
  '/profile-edit',
  '/cart',
  '/checkout',
  '/checkout/3ds-response',
  '/orders',
  '/payment-cards-list',
  '/cards/new',
  '/3ds-redirect',
];

const authRoutes = [
  '/login',
  '/registration',
  '/password-reset',
  '/delete-account',
  '/specialized-stores/welcome',
  '/specialized-stores/verify',
];

const DefaultLayout = lazy(() => import('./DefaultLayout'));
const AuthLayout = lazy(() => import('./AuthLayout'));
const DetailLayout = lazy(() => import('./DetailLayout'));

export default function LayoutWrapper({ children }: LayoutWrapperProps) {
  const { pathname } = useLocation();
  const matchOrderDetail = useMatch('/order-detail/:orderId');
  const matchProductDetail = useMatch('/products/:productSlug/:productId');
  const matchPaymentVerification = useMatch('/3ds-verification/:orderId');
  const matchEditAddress = useMatch('/address/edit/:addressPk');
  const matchWalletPath = useMatch('/wallet/*');
  const matchExternalPaymentVerification = useMatch('/payment-verification/*');
  const matchNPS = useMatch('/nps/users/:user_pk');

  const isPromotionsPage = pathname === '/promociones';
  const isHomePage =
    pathname === '/' || pathname === '/specialized-stores/products';

  const isDetailLayout =
    detailRoutes.includes(pathname) ||
    matchOrderDetail ||
    matchProductDetail ||
    matchPaymentVerification ||
    matchEditAddress ||
    matchWalletPath ||
    isPromotionsPage ||
    matchExternalPaymentVerification;

  if (isHomePage) {
    return (
      <Suspense fallback={<LoaderOverlay />}>
        <HomeLayout>{children}</HomeLayout>
      </Suspense>
    );
  }

  if (isDetailLayout) {
    return (
      <Suspense fallback={<LoaderOverlay />}>
        <DetailLayout>{children}</DetailLayout>
      </Suspense>
    );
  }

  if (matchNPS) {
    return (
      <Suspense fallback={<LoaderOverlay />}>
        <AppLogoLayout logoSize="small">{children}</AppLogoLayout>
      </Suspense>
    );
  }

  if (authRoutes.includes(pathname)) {
    return (
      <Suspense fallback={<LoaderOverlay />}>
        <AuthLayout>{children}</AuthLayout>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoaderOverlay />}>
      <DefaultLayout>{children}</DefaultLayout>
    </Suspense>
  );
}
